import React, { useEffect, useState } from 'react';
import Icon from '../../shared/Icon';
import ProcessDocumentsCard from '../ProcessDocumentsCard';
import PublicacaoPanel from './PublicacaoPanel';
import { UseProposalWorkspaceResult } from '../../../hooks/useProposalWorkspace';
import { WorkspaceActions } from '../ProposalWorkspace';
import { SURFACE_CENTRAL, TONE_BADGE } from '../../../utils/statusColors';
import { fmtDateTime, fmtRelativeTime, getOrgaoDisplay, formatNumeroCompraComAno } from '../../../utils/formatters';
import { procurementService } from '../../../services/api';
import { SIGNERS } from '../../../config/company';
import { useConfirm } from '../../shared/ConfirmDialog';
import { DocumentViewerModal } from '../../shared/DocumentViewerModal';
import { montarZip } from '../../../utils/zipSemCompressao';
import { GRUPOS, grupoDe, rotuloDoTipo, campo as receitaDoCampo } from '../../../design';
import {
  estadoDoDossie, arquivosParaZip, nomesUnicos, grupoComecaRecolhido, docsDoPacote,
  signatariosDe, pendentesComLink, linkDoSignatario, primeiroNome, quemFalta, timelineMinima,
} from './dossie';

/**
 * Estágio DOCUMENTOS — tudo que vira PAPEL: o conteúdo do PDF da proposta, o
 * checklist do que o edital exige, a geração e os documentos já gerados
 * (com assinatura).
 *
 * No editor antigo isso estava espalhado por uma aba inteira: um colapsável
 * escondido com os templates, dois botões grandes no meio, e a lista de
 * documentos no fim — sem nada dizer o que faltava. Aqui a ordem é a do
 * trabalho: o que vai no PDF → o que falta → gerar → o que já existe.
 */

interface Props {
  ws: UseProposalWorkspaceResult;
  actions: WorkspaceActions;
  templates: any[];
  fmt: (v: any) => string;
  /** `replace` abre o modal para trocar o signatário de um envio em andamento. */
  onOpenSignature: (doc?: any, opts?: { replace?: boolean }) => void;
}

const DOC_STATUS: Record<string, { label: string; cls: string; plural: string }> = {
  gerado: { label: 'gerado', cls: TONE_BADGE.progress, plural: 'gerados' },
  aguardando_assinatura: { label: 'aguardando assinatura', cls: TONE_BADGE.info, plural: 'aguardando assinatura' },
  assinado: { label: 'assinado', cls: TONE_BADGE.success, plural: 'assinados' },
  cancelado: { label: 'cancelado', cls: TONE_BADGE.neutral, plural: 'cancelados' },
};

// Rótulo, grupo e ordem dos tipos de documento: design/tiposDeDocumento (um mapa só, com o ProcessDocumentsCard).

/**
 * Grupos do SELETOR de modelos. Declaração e termo são peças diferentes (uma
 * afirma um fato, o outro adere a uma condição) e a lista os intercalava,
 * porque só respeitava `sortOrder`: a de Sustentabilidade, que é declaração,
 * caía depois dos três termos. Agrupar torna o rótulo por linha redundante.
 */
const GRUPOS_TEMPLATE: Array<{ categoria: string; titulo: string }> = [
  { categoria: 'declaracao', titulo: 'Declarações' },
  { categoria: 'termo', titulo: 'Termos' },
];

/** Categoria ausente ou desconhecida conta como declaração. */
const categoriaDe = (c?: string): string => (c === 'termo' ? 'termo' : 'declaracao');

/** "7 assinados" · "5 assinados · 2 gerados" — some quando o grupo tem 1 item. */
function resumoStatus(docs: any[]): string {
  const contagem = new Map<string, number>();
  for (const d of docs) contagem.set(d.status, (contagem.get(d.status) || 0) + 1);
  return Array.from(contagem.entries())
    .map(([st, n]) => {
      const info = DOC_STATUS[st] || DOC_STATUS.gerado;
      return `${n} ${n > 1 ? info.plural : info.label}`;
    })
    .join(' · ');
}

export default function DocumentosSection({ ws, actions, templates, fmt, onOpenSignature }: Props) {
  const confirm = useConfirm(); // §8: confirmação padrão do HUB, não o nativo
  const { form, setForm, generatedDocs, checklist, checklistLoading } = ws;
  const [gerando, setGerando] = useState(false);
  const [painelAberto, setPainelAberto] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  // Visualizador (DocumentViewerModal, o mesmo dos Ofícios). O PDF vem pela rota
  // de download, same-origin, e vira Blob URL: o assinado mora no S3 do ClickSign,
  // cross-origin, e nem sempre abre embutido.
  const [visualizando, setVisualizando] = useState<any | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  // Menu "⋯" da linha: ações raras e destrutivas (trocar signatário, excluir).
  const [menuAberto, setMenuAberto] = useState<number | null>(null);
  const [zipando, setZipando] = useState(false);

  useEffect(() => {
    if (menuAberto == null) return;
    const fechar = () => setMenuAberto(null);
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [menuAberto]);

  useEffect(() => () => { if (viewerUrl) URL.revokeObjectURL(viewerUrl); }, [viewerUrl]);

  const ativos = templates.filter((t: any) => t.isActive !== false);
  // O card de documentos do processo só existe onde o portal PEDE documento no
  // cadastro da proposta (BLL, BNC, PCP). Em ComprasNet não há upload nenhum —
  // e o card aparecia lá do mesmo jeito, com 6 modelos nossos fingindo ser
  // exigência (auditoria 25/08/2026). Quem decide é o backend.
  const exigeDocumentos = checklist?.exigeDocumentos === true;
  const propostaDoc = generatedDocs.find((d: any) => d.type === 'proposta_financeira' && d.status !== 'cancelado');
  const [anexando, setAnexando] = useState<string | null>(null);

  const anexar = async (row: any, file: File) => {
    if (!ws.proposal?.id) return;
    setAnexando(row.key);
    try {
      await procurementService.uploadBllDocument(ws.proposal.id, row.exigencia, !!row.obrigatorio, file);
      actions.showToast(`"${row.exigencia}" anexado`, 'success');
      await ws.reload();
      await ws.reloadChecklist();
    } catch (e: any) {
      actions.showToast('Erro ao anexar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setAnexando(null);
    }
  };
  const protegido = (d: any) => d && (d.status === 'assinado' || d.status === 'aguardando_assinatura');

  const gerar = async (templateIds: number[], incluirProposta: boolean) => {
    if (!ws.proposal?.id) return;
    setGerando(true);
    try {
      if (ws.dirty) await ws.save({ silent: true });
      // Substitui apenas o que está em 'gerado' (assinado/aguardando é intocável).
      // P5 (12/10/2026): a lista vinha do estado da tela, que vira [] quando a leitura falha, e a remoção da versão
      // anterior engolia o erro (`.catch(() => {})`): nos dois casos o documento era gerado DE NOVO ao lado do antigo.
      // Agora relê do servidor antes de substituir; sem a leitura, ou se uma remoção falha, não gera e diz por quê.
      const nomes = ativos.filter((t: any) => templateIds.includes(t.id)).map((t: any) => t.name);
      if (incluirProposta) nomes.push('Proposta Financeira');
      let atuais: any[];
      try {
        const lidos = await procurementService.getGeneratedDocs(ws.proposal.id);
        atuais = Array.isArray(lidos) ? lidos : [];
      } catch (e: any) {
        actions.showToast(`Não gerei: não consegui ler os documentos já gerados (${e?.response?.data?.message || e?.message || 'o servidor não respondeu'}), e gerar sem ler duplicaria os que existem.`, 'error');
        return;
      }
      for (const gd of atuais) {
        if (nomes.includes(gd.name) && gd.status === 'gerado') {
          try {
            await procurementService.deleteGeneratedDoc(gd.id);
          } catch (e: any) {
            actions.showToast(`Não gerei: não consegui remover a versão anterior de "${gd.name}" (${e?.response?.data?.message || e?.message || 'o servidor não respondeu'}); gerar agora deixaria duas.`, 'error');
            await ws.reloadGeneratedDocs();
            return;
          }
        }
      }
      const r = await procurementService.generateAndSave(ws.proposal.id, {
        templateIds,
        includePropostaFinanceira: incluirProposta,
      });
      const ok = r?.results?.filter((x: any) => !x.error).length || 0;
      actions.showToast(`${ok} documento(s) gerado(s)`, 'success');
      setPainelAberto(false);
      setSelecionados(new Set());
      await ws.reloadGeneratedDocs();
      await ws.reloadChecklist();
    } catch (e: any) {
      actions.showToast('Erro ao gerar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setGerando(false);
    }
  };

  /**
   * Nova versão da Proposta Financeira, CONVIVENDO com a anterior (08/10/2026, Dispensa 66/2026, proposta 371). O botão
   * ficava desabilitado com uma proposta assinada ou aguardando assinatura, e o title mandava "exclua antes de regerar":
   * o único caminho para reenviar uma proposta corrigida passava por APAGAR a que foi entregue ao órgão e está no
   * processo. ⛔ Nada assinado é apagado ou sobrescrito para dar lugar ao novo: a anterior continua, como registro do
   * que foi entregue, e a nova nasce 'gerado'. O backend já fazia isso (generate-and-save insere linha nova, com a chave
   * do S3 versionada); quem impedia era só o botão. O `gerar` segue apagando apenas rascunhos ('gerado') de mesmo nome.
   */
  const gerarNovaProposta = async () => {
    if (protegido(propostaDoc) && !(await confirm({
      title: 'Gerar uma nova versão da Proposta Financeira?',
      message: `Já existe uma Proposta Financeira ${DOC_STATUS[propostaDoc.status]?.label}.`,
      consequence: 'A anterior continua como está, como registro do que foi entregue. A nova nasce como "gerado" e segue o fluxo normal de assinatura.',
      confirmLabel: 'Gerar nova versão',
    }))) return;
    await gerar([], true);
  };

  /**
   * Abre o documento no DocumentViewerModal, o mesmo dos Ofícios. Até 03/10/2026
   * era `window.open` numa aba nova: o operador saía do workspace e voltava sem
   * o contexto da lista.
   */
  const abrirDoc = async (d: any) => {
    setMenuAberto(null);
    setVisualizando(d);
    setViewerUrl(null);
    setViewerLoading(true);
    try {
      const blob = await procurementService.downloadGeneratedDoc(d.id);
      setViewerUrl(URL.createObjectURL(blob));
    } catch {
      actions.showToast('Erro ao abrir o PDF', 'error');
      setVisualizando(null);
    } finally {
      setViewerLoading(false);
    }
  };

  // O card de documentos do processo ainda chama por id.
  const verDoc = async (id: number) => {
    const d = generatedDocs.find((x: any) => x.id === id);
    if (d) return abrirDoc(d);
    try {
      const { url } = await procurementService.getGeneratedDocUrl(id);
      window.open(url, '_blank');
    } catch {
      actions.showToast('Erro ao abrir o PDF', 'error');
    }
  };

  /**
   * Link de assinatura do ClickSign, direto da coluna `clicksignSignatureUrl`.
   *
   * ⚠️ `clicksignSignatureUrl` é o link do PRIMEIRO signatário da rodada. Com
   * `signers` gravado, o link sai do `clicksignSignerKey` de cada signatário
   * pendente (`linkDoSignatario`, mesma regra do `linkDeAssinatura`), um botão
   * por pessoa. `clicksignSignatureUrl` fica só para as linhas antigas, sem lista.
   */
  const copiarLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      actions.showToast('Link de assinatura copiado', 'success');
    } catch {
      actions.showToast('Não foi possível copiar. Abra o link e copie da barra de endereço.', 'error');
    }
  };

  /**
   * "Baixar assinados (.zip)": o maço que sobe no anexo do portal. Documento
   * dentro de pacote não entra sozinho (o assinado dele É o pacote); ver
   * `arquivosParaZip`.
   */
  const baixarZipAssinados = async () => {
    const docs = arquivosParaZip(generatedDocs);
    if (docs.length === 0) return;
    setZipando(true);
    try {
      const nomes = nomesUnicos(docs.map((d: any) => d.name));
      const arquivos = [];
      for (let i = 0; i < docs.length; i++) {
        const blob = await procurementService.downloadGeneratedDoc(docs[i].id);
        arquivos.push({ nome: nomes[i], dados: new Uint8Array(await blob.arrayBuffer()) });
      }
      const zip = montarZip(arquivos);
      const it = ws.item || {};
      const ref = formatNumeroCompraComAno(it.numeroCompra, it.anoCompra);
      const nomeZip = `Assinados${ref && ref !== '—' ? ' — ' + ref.replace(/[\\/:*?"<>|]/g, '-') : ''}.zip`;
      const url = URL.createObjectURL(new Blob([zip], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeZip;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      actions.showToast(`${docs.length} PDF(s) assinado(s) no .zip`, 'success');
    } catch (e: any) {
      actions.showToast('Erro ao montar o .zip: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setZipando(false);
    }
  };


  const excluirDoc = async (d: any) => {
    if (protegido(d) && !(await confirm({
      title: 'Excluir documento protegido?',
      message: `"${d.name}" está ${DOC_STATUS[d.status]?.label}.`,
      consequence: 'Documento assinado ou aguardando assinatura some do card e da publicação para o órgão.',
      danger: true,
      confirmLabel: 'Excluir',
    }))) return;
    try {
      await procurementService.deleteGeneratedDoc(d.id);
      await ws.reloadGeneratedDocs();
      await ws.reloadChecklist();
    } catch (e: any) {
      actions.showToast('Erro ao excluir: ' + (e?.response?.data?.message || e?.message), 'error');
    }
  };

  /**
   * Baixa o PDF ASSINADO. O nome do arquivo sai do nome do documento, com
   * sufixo `_assinado`, para o operador reconhecer o que está anexando no
   * portal sem abrir — na habilitação ele sobe cinco ou seis de uma vez.
   */
  const baixarAssinado = async (d: any) => {
    try {
      const blob = await procurementService.downloadGeneratedDoc(d.id);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(d.name || 'documento').replace(/[\\/:*?"<>|]/g, '-')}_assinado.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e: any) {
      actions.showToast('Erro ao baixar o PDF assinado: ' + (e?.response?.data?.message || e?.message), 'error');
    }
  };

  /**
   * O secundário da linha diz o que decide a próxima ação: há quanto tempo o
   * documento está parado, e não a data de criação repetida.
   *
   * ⚠️ "Quem assina" ainda não sai daqui: `procurement_generated_documents` não
   * guarda o signatário (só `sentForSignatureBy`, que é quem ENVIOU). Escrever
   * "Fábio" fixo mentiria depois de um "Trocar signatário". O nome entra na
   * etapa 4, com a coluna `signers` do backend.
   */
  const secundario = (d: any): string => {
    const tipo = rotuloDoTipo(d.type);
    const partes: string[] = [];
    if (d.type === 'pacote_assinatura') {
      const n = docsDoPacote(d, generatedDocs);
      partes.push(n > 0 ? `${n} ${n === 1 ? 'documento' : 'documentos'} · 1 envelope ClickSign` : tipo);
    } else {
      partes.push(tipo);
    }
    if (d.status === 'aguardando_assinatura') {
      const desde = d.sentForSignatureAt;
      // Com `signers` (envios desde 02/10/2026) diz QUEM falta; nas linhas
      // antigas a lista é null e o texto fica genérico, sem chutar nome.
      const quem = quemFalta(signatariosDe(d)) || 'aguardando assinatura';
      partes.push(desde ? `${quem} desde ${fmtDateTime(desde)} · ${fmtRelativeTime(desde)}` : quem);
    } else if (d.status === 'assinado') {
      if (d.signedAt) partes.push(`assinado ${fmtDateTime(d.signedAt)}`);
    } else if (d.status === 'gerado') {
      partes.push(`não enviado para assinatura · gerado ${fmtRelativeTime(d.createdAt)}`);
    }
    return partes.join(' · ');
  };

  const linhaDoc = (d: any) => {
    const st = DOC_STATUS[d.status] || DOC_STATUS.gerado;
    const aguardando = d.status === 'aguardando_assinatura';
    const signers = signatariosDe(d);
    const pendentes = aguardando ? pendentesComLink(signers) : [];
    // Um signatário pendente: o botão de sempre. Mais de um: um por pessoa.
    // Sem lista (linha antiga): a URL do primeiro da rodada, como antes.
    const links: { rotulo: string; url: string; quem?: string }[] =
      pendentes.length > 1
        ? pendentes.map((s) => ({ rotulo: primeiroNome(s.name), url: linkDoSignatario(s)!, quem: s.name }))
        : pendentes.length === 1
          ? [{ rotulo: 'Link de assinatura', url: linkDoSignatario(pendentes[0])!, quem: pendentes[0].name }]
          : aguardando && !signers && d.clicksignSignatureUrl
            ? [{ rotulo: 'Link de assinatura', url: d.clicksignSignatureUrl }]
            : [];
    return (
      <div key={d.id} className="bg-central-elevado rounded-lg border border-central-borda px-3 py-2 flex items-center gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-slate-200 truncate">{d.name}</div>
          <div className="text-[10px] text-slate-500">{secundario(d)}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.cls}`}>{st.label}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => abrirDoc(d)}
            className="px-2 py-1 text-[10px] border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded transition-colors"
          >
            Abrir
          </button>
          {/* O PDF assinado é o arquivo que sobe no portal, e até 24/09/2026 só
              dava para chegar nele pela outra tela de documentos. O backend faz o
              proxy do PDF da ClickSign (cross-origin sem CORS) e devolve
              same-origin com Content-Disposition: attachment. */}
          {d.status === 'assinado' && (
            <button
              onClick={() => baixarAssinado(d)}
              className="px-2 py-1 text-[10px] border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded transition-colors"
              title="Baixa o PDF assinado, pronto para anexar no portal"
            >
              ↓ Assinado
            </button>
          )}
          {d.status === 'gerado' && (
            <button onClick={() => onOpenSignature(d)} className="px-2 py-1 text-[10px] border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded transition-colors">Assinar</button>
          )}
          {/* Link direto do ClickSign: o e-mail do ClickSign nem sempre chega
              (OF-2026/059, 28/09/2026), e o link é o que o signatário usa. */}
          {links.map((l) => (
            <span key={l.url} className="inline-flex rounded overflow-hidden">
              <button
                onClick={() => copiarLink(l.url)}
                className="px-2 py-1 text-[10px] bg-central-cartao hover:bg-central-elevado text-central-texto border border-central-borda transition-colors"
                title={`Copia o link de assinatura do ClickSign${l.quem ? ' de ' + l.quem : ''}\n${l.url}`}
              >
                🔗 {l.rotulo}
              </button>
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-1.5 py-1 text-[10px] bg-central-cartao hover:bg-central-elevado text-central-texto border border-l-0 border-central-borda transition-colors"
                title="Abrir o link de assinatura"
              >
                ↗
              </a>
            </span>
          ))}
          {/* Ações raras e destrutivas no menu "⋯": a lixeira não fica mais ao
              lado do botão que mais se clica. */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setMenuAberto(menuAberto === d.id ? null : d.id); }}
              className="px-1.5 py-1 text-[11px] leading-none text-slate-400 hover:text-white hover:bg-central-elevado rounded transition-colors"
              title="Mais ações"
              aria-haspopup="menu"
              aria-expanded={menuAberto === d.id}
            >
              ⋯
            </button>
            {menuAberto === d.id && (
              <div
                role="menu"
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-full mt-1 z-20 min-w-[11rem] bg-central-cartao border border-central-borda rounded-lg shadow-xl py-1"
              >
                {aguardando && (
                  <button
                    role="menuitem"
                    onClick={() => { setMenuAberto(null); onOpenSignature(d, { replace: true }); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-central-elevado"
                    title="Cancela na ClickSign e reenvia o mesmo PDF para outro signatário"
                  >
                    Trocar signatário
                  </button>
                )}
                <button
                  role="menuitem"
                  onClick={() => { setMenuAberto(null); excluirDoc(d); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-red-300 hover:bg-central-elevado flex items-center gap-2"
                >
                  <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const campo = (label: string, key: keyof typeof form, placeholder?: string, textarea?: boolean) => (
    <div key={String(key)}>
      <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">{label}</label>
      {textarea ? (
        <textarea
          value={String(form[key] ?? '')}
          onChange={(e) => setForm({ [key]: e.target.value } as any)}
          rows={3}
          className={`w-full rounded-lg px-3 py-1.5 text-sm resize-y outline-none ${receitaDoCampo}`}
          placeholder={placeholder}
        />
      ) : (
        <input
          value={String(form[key] ?? '')}
          onChange={(e) => setForm({ [key]: e.target.value } as any)}
          className={`w-full rounded-lg px-3 py-1.5 text-sm outline-none ${receitaDoCampo}`}
          placeholder={placeholder}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* 1. O QUE VAI NO PDF */}
      <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
        <div className="flex items-center gap-2 text-sm text-slate-200 font-medium">
          <Icon name="fileText" className="w-4 h-4 text-slate-400" />
          Conteúdo do PDF da proposta
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campo('Título', 'title', 'Proposta — Órgão')}
          {campo('Validade (dias)', 'validityDays', '60')}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">
            Objeto da contratação
            {!form.description && ws.item?.objetoCompra && (
              <span className="text-slate-600 normal-case tracking-normal ml-2">(texto do edital — edite se quiser outro no PDF)</span>
            )}
          </label>
          <textarea
            value={form.description || ws.item?.objetoCompra || ''}
            onChange={(e) => setForm({ description: e.target.value })}
            rows={3}
            className={`w-full rounded-lg px-3 py-1.5 text-sm resize-y outline-none ${receitaDoCampo}`}
            placeholder="Objeto que aparece no PDF…"
          />
        </div>

        <details className="bg-central-elevado rounded-lg border border-central-borda">
          <summary className="px-3 py-2 text-xs text-slate-400 cursor-pointer hover:text-slate-200">
            Dados comerciais e textos adicionais
          </summary>
          <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
            {campo('Representante legal', 'representative', SIGNERS.fabio.name)}
            {campo('Cargo', 'representativeRole', SIGNERS.fabio.cargo)}
            {campo('Dados bancários', 'bankInfo')}
            {campo('Marca', 'brand')}
            {campo('Modelo', 'model')}
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">Formato da planilha</label>
              <select
                value={String(form.pdfLayout || '')}
                onChange={(e) => setForm({ pdfLayout: e.target.value })}
                className={`w-full rounded-lg px-3 py-1.5 text-sm outline-none ${receitaDoCampo}`}
              >
                <option value="">Automático (pelos itens)</option>
                <option value="item_unico">Item único</option>
                <option value="multi_grupo">Itens agrupados por grupo/lote</option>
                <option value="planilha_orgao">Planilha do órgão (uma tabela por item, com subtotal)</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-slate-300 pb-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(form.pdfIncluirTributacao)}
                  onChange={(e) => setForm({ pdfIncluirTributacao: e.target.checked })}
                  className="accent-primary"
                />
                Incluir seção de tributação (Simples Nacional)
              </label>
            </div>
            <div className="md:col-span-2">{campo('Observações', 'observations', 'Viram a Especificação Técnica do PDF…', true)}</div>
            <div className="md:col-span-2">{campo('Condições', 'conditions', 'Uma condição por linha; substituem as padrão…', true)}</div>
          </div>
        </details>
      </div>

      {/* 2. O QUE O PORTAL EXIGE ANEXAR — só onde existe upload de documento.
             Antes era "Checklist de Declarações" em todo portal, com os 6
             modelos padrão da IPCOM listados como se fossem exigência. */}
      {exigeDocumentos && (
        <ProcessDocumentsCard
          checklist={checklist}
          loading={checklistLoading}
          templates={templates}
          onReload={() => ws.reloadChecklist()}
          onLinkTemplate={async (row, templateId) => {
            if (!ws.proposal?.id) return;
            try {
              await procurementService.setChecklistOverride(ws.proposal.id, { key: row.key, templateId });
              await ws.reloadChecklist();
            } catch {
              actions.showToast('Erro ao vincular declaração', 'error');
            }
          }}
          onToggleOverride={async (row, status) => {
            if (!ws.proposal?.id) return;
            const jaTem = row.override?.status === status;
            try {
              await procurementService.setChecklistOverride(ws.proposal.id, { key: row.key, status: jaTem ? null : status });
              await ws.reloadChecklist();
            } catch {
              actions.showToast('Erro ao marcar exigência', 'error');
            }
          }}
          onGenerate={(row) => { if (row.templateId) gerar([row.templateId], false); }}
          onViewDoc={verDoc}
          onUpload={anexar}
          uploadingKey={anexando}
        />
      )}

      {/* 3. GERAR */}
      <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-slate-200 font-medium">Gerar documentos</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={gerarNovaProposta}
              disabled={gerando}
              title={protegido(propostaDoc) ? `Gera uma NOVA versão; a ${DOC_STATUS[propostaDoc.status]?.label} continua como está` : undefined}
              className="px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-40 rounded-lg text-xs font-medium transition-colors"
            >
              {gerando ? 'Gerando…' : 'Proposta Financeira'}
            </button>
            <button
              type="button"
              onClick={() => setPainelAberto((v) => !v)}
              disabled={gerando}
              className="px-3 py-2 bg-central-cartao border border-central-borda hover:bg-central-elevado disabled:opacity-40 text-slate-200 rounded-lg text-xs transition-colors"
            >
              Declarações e termos…
            </button>
          </div>
        </div>

        {painelAberto && (
          <div className="bg-central-elevado rounded-lg border border-central-borda p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap text-[10px]">
              <button
                onClick={() => setSelecionados(new Set(ativos.filter((t: any) => t.isDefault).map((t: any) => t.id)))}
                className="px-2 py-0.5 rounded-full bg-slate-600/30 text-slate-300 hover:bg-central-elevado/50"
              >⭐ Padrão ({ativos.filter((t: any) => t.isDefault).length})</button>
              <button
                onClick={() => setSelecionados(new Set(ativos.map((t: any) => t.id)))}
                className="px-2 py-0.5 rounded-full bg-central-elevado text-slate-300 hover:bg-central-elevado"
              >Todas</button>
              <button
                onClick={() => setSelecionados(new Set())}
                className="px-2 py-0.5 rounded-full text-slate-500 hover:text-slate-300"
              >Limpar</button>
            </div>
            <div className="max-h-[40vh] overflow-y-auto space-y-3">
              {GRUPOS_TEMPLATE.map(({ categoria, titulo }) => {
                const doGrupo = ativos.filter((t: any) => categoriaDe(t.category) === categoria);
                if (doGrupo.length === 0) return null;
                const marcados = doGrupo.filter((t: any) => selecionados.has(t.id)).length;
                return (
                  <div key={categoria} className="space-y-1">
                    <div className="flex items-center gap-2 px-2">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{titulo}</span>
                      <span className="text-[10px] text-slate-600">
                        {marcados > 0 ? `${marcados}/${doGrupo.length}` : doGrupo.length}
                      </span>
                      <div className="flex-1 h-px bg-central-elevado" />
                    </div>
                    {doGrupo.map((t: any) => {
                      const existente = generatedDocs.find((d: any) => d.name === t.name && d.status !== 'cancelado');
                      const trava = protegido(existente);
                      return (
                        <label
                          key={t.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded ${trava ? 'opacity-50' : 'hover:bg-central-elevado cursor-pointer'}`}
                          title={trava ? `Já está ${DOC_STATUS[existente.status]?.label}` : undefined}
                        >
                          <input
                            type="checkbox"
                            disabled={trava}
                            checked={selecionados.has(t.id)}
                            onChange={(e) => {
                              const next = new Set(selecionados);
                              if (e.target.checked) next.add(t.id); else next.delete(t.id);
                              setSelecionados(next);
                            }}
                            className="rounded border-slate-500 bg-slate-700"
                          />
                          <span className="text-xs text-slate-300 flex-1">{t.title || t.name}</span>
                          {existente && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${DOC_STATUS[existente.status]?.cls}`}>
                              {DOC_STATUS[existente.status]?.label}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPainelAberto(false)} className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200">Cancelar</button>
              <button
                onClick={() => gerar(Array.from(selecionados), false)}
                disabled={selecionados.size === 0 || gerando}
                className="px-4 py-1.5 bg-primary hover:bg-primary-strong disabled:opacity-40 text-white rounded-lg text-xs font-medium transition-colors"
              >
                Gerar {selecionados.size > 0 ? `(${selecionados.size})` : ''}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 4. O QUE JÁ EXISTE — o cabeçalho responde, sem abrir nada: dá para
             subir no portal? */}
      {(() => {
        const estado = estadoDoDossie(generatedDocs);
        const noZip = arquivosParaZip(generatedDocs).length;
        return (
          <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
            <div className="flex items-center gap-3 flex-wrap pb-3 border-b border-central-borda">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-200 font-medium">
                  Documentos gerados <span className="text-slate-500 font-normal">({generatedDocs.length})</span>
                </div>
                {estado.total > 0 && (
                  <div className={`text-[11px] mt-0.5 ${estado.prontoParaPortal ? 'text-white font-medium' : 'text-slate-400'}`}>
                    {estado.texto}
                  </div>
                )}
              </div>
              {estado.total > 0 && (
                <div
                  className="w-28 h-1.5 rounded-full bg-central-elevado overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={estado.total}
                  aria-valuenow={estado.assinados}
                  title={`${estado.assinados} de ${estado.total} assinados`}
                >
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.round(estado.progresso * 100)}%` }} />
                </div>
              )}
              <div className="flex items-center gap-2">
                {noZip > 0 && (
                  <button
                    onClick={baixarZipAssinados}
                    disabled={zipando}
                    className="px-3 py-1.5 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50 rounded-lg text-xs transition-colors"
                    title="Junta os PDFs assinados num .zip, o maço que sobe no anexo do portal. Documento de pacote vai dentro do pacote."
                  >
                    {zipando ? 'Montando .zip…' : `↓ Baixar assinados (.zip)`}
                  </button>
                )}
                {generatedDocs.length > 0 && (
                  <button
                    onClick={() => onOpenSignature(undefined)}
                    className="px-3 py-1.5 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-xs transition-colors"
                  >
                    Enviar para assinatura…
                  </button>
                )}
              </div>
            </div>

            {generatedDocs.length === 0 && (
              <div className="text-xs text-slate-500">Nenhum documento gerado ainda.</div>
            )}

            <div className="space-y-3">
              {GRUPOS.map(({ tipo, titulo }) => {
                const docs = generatedDocs.filter((d: any) => grupoDe(d.type) === tipo);
                if (docs.length === 0) return null;
                // Grupo resolvido recolhe; grupo com pendência abre (ver
                // `grupoComecaRecolhido`). Antes só recolhia por volume.
                const recolher = grupoComecaRecolhido(docs);
                return (
                  <details key={tipo} open={!recolher}>
                    <summary className="px-1 py-1 cursor-pointer text-xs text-slate-300 hover:text-slate-100 transition-colors">
                      <span className="font-medium">{titulo}</span>
                      <span className="text-slate-500 ml-2 font-normal">
                        {docs.length} {docs.length > 1 ? 'documentos' : 'documento'}
                        {` · ${recolher && docs.every((d: any) => d.status === 'assinado') ? 'todos assinados' : resumoStatus(docs)}`}
                      </span>
                    </summary>
                    <div className="space-y-2 mt-2">{docs.map(linhaDoc)}</div>
                  </details>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* 5. O QUE VAI PARA O ÓRGÃO */}
      {ws.item?.id && (
        <PublicacaoPanel
          itemId={ws.item.id}
          generatedDocs={generatedDocs}
          showToast={actions.showToast}
        />
      )}

      {/* Visualizador, com os painéis dos Ofícios: Signatários (de `signers`,
          herdado do pacote quando a declaração não tem lista própria) e
          Histórico, que aparece TAMBÉM no documento já assinado. */}
      {visualizando && (() => {
        const d = visualizando;
        const st = DOC_STATUS[d.status] || DOC_STATUS.gerado;
        const it = ws.item || {};
        const ref = formatNumeroCompraComAno(it.numeroCompra, it.anoCompra);
        const licitacao = [String(it.modalidadeNome || '').split(' ')[0], ref !== '—' ? ref : ''].filter(Boolean).join(' ') || '—';
        const tom = d.status === 'assinado' ? 'success' as const : d.status === 'aguardando_assinatura' ? 'warning' as const : d.status === 'cancelado' ? 'muted' as const : 'default' as const;
        return (
          <DocumentViewerModal
            title={d.name || 'Documento'}
            subtitle={rotuloDoTipo(d.type)}
            fileUrl={viewerUrl}
            mimeType="application/pdf"
            loading={viewerLoading}
            accent="orange"
            onClose={() => { setVisualizando(null); setViewerUrl(null); }}
            info={[
              { label: 'Status', value: st.label, tone: tom },
              { label: 'Tipo', value: rotuloDoTipo(d.type) },
              { label: 'Licitação', value: licitacao },
              { label: 'Órgão', value: getOrgaoDisplay(it) || '—' },
              { label: 'Gerado em', value: d.createdAt ? fmtDateTime(d.createdAt) : '—' },
              ...(d.signedAt ? [{ label: 'Assinado em', value: fmtDateTime(d.signedAt), tone: 'success' as const }] : []),
            ]}
            signers={signatariosDe(d) || undefined}
            // Lista null = documento anterior a 02/10/2026: "não registrado", em
            // palavras, e não um painel vazio com cara de defeito.
            sidebarExtra={
              !signatariosDe(d) && d.status !== 'gerado' ? (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Signatários</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Não registrados neste documento. Ele foi enviado antes de 02/10/2026, quando o HUB ainda
                    não guardava quem assina; a lista está na ClickSign.
                  </p>
                </div>
              ) : null
            }
            documentSignedAt={d.signedAt || null}
            timeline={timelineMinima(d, signatariosDe(d))}
            // Só para linha antiga, sem lista: o modal ignora signatureUrl
            // quando algum signatário tem link próprio (ver `copiarLink`).
            signatureUrl={d.status === 'aguardando_assinatura' ? d.clicksignSignatureUrl || null : null}
            headerActions={
              d.status === 'assinado' ? (
                <button
                  onClick={() => baixarAssinado(d)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto"
                  title="Baixa o PDF assinado"
                >
                  ↓ Assinado
                </button>
              ) : null
            }
          />
        );
      })()}
    </div>
  );
}
